import { Obligation, ReserveType } from "@renec-foundation/relend-adapter"
import { PublicKey } from "@solana/web3.js"
import { Getter, atom } from "jotai"
import { atomWithReducer, selectAtom } from "jotai/utils"
import { StaticImageData } from "next/image"
import { poolsAtom, selectedMainPoolAtom } from "./pools"
import { selectedSolendPoolAtom } from "./solendPools"

export type RawObligationType = {
  pubkey: PublicKey
  info: Obligation | null
}

export type ReserveWithMetadataType = ReserveType & {
  symbol: string
  logo: SelectedPoolLogoType
}

export type SelectedReserveType = ReserveType & {
  name: string | null
  symbol: string
  logo: SelectedPoolLogoType
}

export type SelectedPoolType = {
  name: string | null
  address: string
  authorityAddress: string
  owner: string
  reserves: Array<SelectedReserveType>
}

export type SelectedPoolLogoType = string | null | StaticImageData

export const uniqueAssetsAtom = selectAtom(
  poolsAtom,
  (pools) => {
    const assets = Object.values(pools).flatMap((p) => p.reserves.map((r) => r.mintAddress))
    return assets.filter((item, pos) => assets.indexOf(item) === pos)
  },
  (a, b) => {
    const sortB = b.sort()
    return Boolean(a.length) && a.sort().every((val, index) => val === sortB[index])
  },
)

export const publicKeyAtom = atom<string | null>(null)

// We only use reserves in the pools, so we can merge them here
export const selectedPoolAtom = atom((get) => {
  const mainPool = get(selectedMainPoolAtom)
  const solendPool = get(selectedSolendPoolAtom)
  const mergedReserves = mainPool?.reserves.concat(solendPool?.reserves || []) || []
  return {
    ...mainPool,
    reserves: mergedReserves,
  }
})

export function atomWithRefresh<T>(fn: (get: Getter) => T) {
  const refreshCounter = atom(0)

  return atom(
    (get) => {
      get(refreshCounter)
      return fn(get)
    },
    async (_, set) => set(refreshCounter, (i) => i + 1),
  )
}

export function atomWithCompare<Value>(initialValue: Value, areEqual: (prev: Value, next: Value) => boolean) {
  return atomWithReducer(initialValue, (prev: Value, next: Value) => {
    if (areEqual(prev, next)) {
      return prev
    }

    return next
  })
}
